/**
 * The dot that flies from a pressed button to the basket — the rule half.
 *
 * **It is inferred, not wired.** Legacy watches the last pointer-down anywhere in the document and the
 * cart's total, and animates when the total goes up shortly after a press. That is a good design and it
 * is kept: wiring an animation into every call site means every future call site has to remember, and
 * the ones that forget are invisible.
 *
 * The window matters. Too short and a slow render misses it; too long and an add that happened for some
 * other reason — a restored basket, a gift the server granted — flies from wherever the customer last
 * happened to touch. Legacy's own 800 ms is kept.
 */

export const RECENT_PRESS_MS = 800

export interface Press {
  x: number
  y: number
  at: number
}

/**
 * Whether an increase should be animated, and from where.
 *
 * `null` for anything that is not a recent press followed by an increase — which is the ordinary case
 * on load, when the persisted basket is read back and nothing was pressed at all.
 */
export function flightFrom(
  press: Press | null,
  previousCount: number,
  nextCount: number,
  now: number,
): Press | null {
  if (press === null) return null
  if (nextCount <= previousCount) return null
  if (now - press.at > RECENT_PRESS_MS) return null
  // A press recorded in the future is a clock that moved; treat it as no press rather than animate
  // from a position that may be stale.
  if (now < press.at) return null
  return press
}

/**
 * Whether an element counts as the thing that was pressed.
 *
 * The nearest button-like ancestor, because a press usually lands on an icon or a label inside one and
 * animating from the icon's own box starts the dot in the wrong place.
 */
export function pressTarget(element: Element | null): Element | null {
  if (element === null) return null
  return element.closest("button, a, [role='button']") ?? element
}
